import { InferSubmit } from '@if/core/yup'
import {
  type FormContainerOnError,
  type FormDraftValues,
  type FormSchema,
  type FormSetup,
  type FormSubmitValues,
} from '@if/mui'
import { Stack } from '@mui/material'
import { useUpdateMerchant, type UpdateMerchantDto } from '@src/data/api/merchants-api/merchants-api'
import { EMerchantProperties, EMerchantReportFrequency } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { AxiosError } from 'axios'
import { useCallback, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { buildUpdateMerchantDto } from '../buildUpdateMerchantDto'
import MerchantPropertiesPanel, { MerchantPropertiesSchema } from '../MerchantPropertiesPanel'
import MerchantRatesPanel from '../MerchantRatesPanel'
import MerchantReportConfigPanel, { MerchantReportFormSchema } from '../MerchantReportConfigPanel'
import MerchantTagsPanel, { MerchantTagsSchema } from '../MerchantTagsPanel'
import MerchantTerritory, { MerchantTerritorySchema } from '../MerchantTerritory'
import { useSyncFormDefaults } from '../useSyncFormDefaults'

export const MerchantGeneralFormSchema = MerchantPropertiesSchema.concat(MerchantTagsSchema)
  .concat(MerchantTerritorySchema)
  .concat(MerchantReportFormSchema)

type MerchantGeneralFormSchemaType = typeof MerchantGeneralFormSchema

export type MerchantGeneralFormValues = InferSubmit<MerchantGeneralFormSchemaType>

const merchantPropertyKeys = Object.values(EMerchantProperties)

function getMerchantGeneralDefaults(merchant: Merchant): MerchantGeneralFormValues {
  const properties = merchantPropertyKeys.reduce(
    (acc, key) => {
      acc[key] = merchant[key]
      return acc
    },
    {} as Record<EMerchantProperties, boolean>,
  )

  return {
    tags: merchant.tags ?? [],
    territoryId: merchant.territoryId,
    reportGenerationFrequency: merchant.reportGenerationFrequency,
    reportRecipientsEmailList: merchant.reportRecipientsEmailList,
    ...properties,
  }
}

function toGeneralOverrides(values: MerchantGeneralFormValues): Partial<UpdateMerchantDto> {
  return {
    territoryId: values.territoryId,
    tags: values.tags,
    reportGenerationFrequency: !values.reportGenerationFrequency
      ? EMerchantReportFrequency.never
      : values.reportGenerationFrequency,
    reportRecipientsEmailList: values.reportRecipientsEmailList,
    noPaymentPolicy: values.noPaymentPolicy,
    hideAddress: values.hideAddress,
    canSkipFlinks: values.canSkipFlinks,
    canPayBorrowers: values.canPayBorrowers,
    automaticIncomeApproval: values.automaticIncomeApproval,
    canAcceptHighEstimate: values.canAcceptHighEstimate,
    showInterestRateOnMerchantDashboard: values.showInterestRateOnMerchantDashboard,
  }
}

interface MerchantGeneralTabContentProps {
  merchant: Merchant
  defaults: MerchantGeneralFormValues
}

const MerchantGeneralTabContent = ({ merchant, defaults }: MerchantGeneralTabContentProps) => {
  useSyncFormDefaults<MerchantGeneralFormValues>(defaults)

  return (
    <Stack gap={2}>
      <MerchantTerritory merchant={merchant} />
      <MerchantTagsPanel />
      <MerchantRatesPanel merchant={merchant} />
      <MerchantReportConfigPanel />
      <MerchantPropertiesPanel bannerOwnedFields={merchant.bannerOwnedFields ?? []} />
    </Stack>
  )
}

/**
 * The form methods a tab body and its save receive: draft values while editing,
 * validated submit values once the schema has run. `watch` is omitted to match @if/mui's
 * FormContainer render-prop type (use `useWatch`/`useFormContext` in child components instead).
 */
export type TabFormMethods<S extends FormSchema> = Omit<
  UseFormReturn<FormDraftValues<S>, unknown, FormSubmitValues<S>>,
  'watch'
>

/** A tab's form config, keyed on its yup schema — `MultiFormTab` infers the value types from it. */
export interface MerchantTabConfig<S extends FormSchema> {
  useFormSetup: () => FormSetup<S>
  onSubmit: (values: FormSubmitValues<S>, methods: TabFormMethods<S>) => unknown
  onError?: FormContainerOnError
  children: (methods: TabFormMethods<S>) => React.ReactNode
}

/**
 * Builds the General tab's form config (schema/defaults/save) and body. Returned
 * as a config object so EditMerchant can spread it onto a `<MultiFormTab>`.
 */
export function useMerchantGeneralTab(merchant: Merchant): MerchantTabConfig<MerchantGeneralFormSchemaType> {
  const { t } = useTranslation()
  const [updateMerchant] = useUpdateMerchant()

  const defaults = useMemo(() => getMerchantGeneralDefaults(merchant), [merchant])

  const useFormSetup = useCallback(
    (): FormSetup<MerchantGeneralFormSchemaType> => ({
      schema: MerchantGeneralFormSchema,
      defaultValues: defaults,
    }),
    [defaults],
  )

  const onSubmit = useCallback(
    async (values: MerchantGeneralFormValues, methods: TabFormMethods<MerchantGeneralFormSchemaType>) => {
      await updateMerchant(buildUpdateMerchantDto(merchant, toGeneralOverrides(values)))
      methods.reset(values)
    },
    [merchant, updateMerchant],
  )

  // These are string-body 400s (kind `unknown`); map the known prose to friendly
  // inline text, and defer anything else to the app registry.
  const onError = useMemo<FormContainerOnError>(
    () => ({
      unknown: ({ raw }) => {
        const data = (raw as AxiosError<string>)?.response?.data
        if (data === 'Dashboard access required for High Estimate') {
          return { role: 'contextual', message: t('common.errors.dashboardAccessRequiredForHighEstimate') }
        }
        if (typeof data === 'string' && data.includes('owned by the parent banner')) {
          // Extracts the field list after the last colon, e.g. "Banner owned fields: Foo, Bar." -> "Foo, Bar"
          const fields = /:\s*(.+?)\.?\s*$/.exec(data)?.[1] ?? null
          if (fields !== null) {
            return { role: 'contextual', message: t('merchant.bannerOwnedWriteGuardError', { fields }) }
          }
        }
      },
    }),
    [t],
  )

  const children = useCallback(
    () => <MerchantGeneralTabContent merchant={merchant} defaults={defaults} />,
    [merchant, defaults],
  )

  return { useFormSetup, onSubmit, onError, children }
}
